import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const Business = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    "business_management",
  );
  const savedFilters = JSON.parse(
    sessionStorage.getItem("businessFilters") || "{}",
  );

  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteBusinessId, setDeleteBusinessId] = useState(null);
  const [deleteBusinessName, setDeleteBusinessName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [status, setStatus] = useState("active");

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [businessId, setBusinessId] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const fetchBusiness = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload = {
        action: "get-all-business",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      if (activeSearchQuery?.trim()) {
        payload.search = activeSearchQuery.trim();
      }

      const res = await axios.post(API_URL, payload);

      let data = res.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      console.log("Business API Response:", data);

      if (data.success) {
        // Your API response is:
        // data.data.businesses
        const businessList = data.data?.businesses || [];

        setBusinesses(businessList);
        setFilteredBusinesses(businessList);

        setTotalCount(
          data.data?.pagination?.totalCount || businessList.length || 0,
        );
      } else {
        toast.error(data.message || "Failed to load business");

        setBusinesses([]);
        setFilteredBusinesses([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching business:", error);

      toast.error(error.response?.data?.message || "Error fetching business");

      setBusinesses([]);
      setFilteredBusinesses([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);

  const startItem =
    businesses.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;

  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,

          behavior: "smooth",
        });
      }, 100);
    }
  };

  useEffect(() => {
    sessionStorage.setItem(
      "businessFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
  ]);

  const handleCreateBusiness = () => {
    setBusinessId(null);
    setBusinessName("");
    setStatus("active");
    setShowBusinessModal(true);
  };
  const handleEditBusiness = (business) => {
    setBusinessId(business._id);
    setBusinessName(business.businessName);
    setStatus(business.status || "active");
    setShowBusinessModal(true);
  };

  const handleViewBusiness = (business) => {
    setSelectedBusiness(business);
    setShowViewModal(true);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }

        pages.push("ellipsis", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, "ellipsis");

        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1, "ellipsis");

        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }

        pages.push("ellipsis", totalPages);
      }
    }

    return pages;
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;

    setSearchQuery(val);

    if (!val.trim() && activeSearchQuery) {
      setActiveSearchQuery("");

      setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());

    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      handleSearchTrigger();
    }
  };

  const handleSaveBusiness = async () => {
    if (!businessName.trim()) {
      toast.error("Business name is required");

      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        action: businessId ? "update-business" : "create-business",
        status,
        businessName: businessName.trim(),
        currentUserId: localStorage.getItem("id"),
      };

      if (businessId) {
        payload.id = businessId;
      }
      const res = await axios.post(API_URL, payload);
      let data = res.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        toast.success(
          businessId
            ? "Business updated successfully"
            : "Business created successfully",
        );

        setShowBusinessModal(false);
        setBusinessName("");
        setBusinessId(null);
        fetchBusiness();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (error) {
      console.log(error);

      toast.error("Error saving business");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);

      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <i className="fa-solid fa-sort text-muted ml-1" />;
    }

    return sortOrder === "asc" ? (
      <i className="fa-solid fa-sort-up text-primary ml-1" />
    ) : (
      <i className="fa-solid fa-sort-down text-primary ml-1" />
    );
  };
  const handleDeleteClick = (id, name) => {
    setDeleteBusinessId(id);
    setDeleteBusinessName(name);
  };

  const handleBusinessPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));

    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!deleteBusinessId) return;

    const currentUserId = localStorage.getItem("id");

    if (!currentUserId) {
      toast.error("Session expired. Please login again.");

      navigate("/");

      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-business",
        id: deleteBusinessId,
        updatedBy: currentUserId,
      };

      const res = await axios.post(API_URL, payload);

      let data = res.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        toast.success("Business deactivated");

        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeleteBusinessId(null);
        setDeleteBusinessName("");

        if (businesses.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchBusiness();
        }
      } else {
        toast.error(data.message || "Failed to delete business");
      }
    } catch (error) {
      console.log(error);

      toast.error("Error deleting business");
    } finally {
      setIsDeleting(false);
    }
  };

  const capitalize = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "-";
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Business</h1>
            </div>

            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <button className="btn btn-blue" onClick={handleCreateBusiness}>
                  Create Business
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search business..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />

                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 text-right">
                    {activeSearchQuery && (
                      <span className="ml-2 text-muted">
                        (Showing:
                        {filteredBusinesses.length})
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : businesses.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no businesses yet.
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered txt_lft">
                      <thead>
                        <tr>
                          <th>S.No</th>

                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("businessName")}
                          >
                            Business Name
                            {getSortIcon("businessName")}
                          </th>

                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("status")}
                          >
                            Status
                            {getSortIcon("status")}
                          </th>

                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredBusinesses.map((business, index) => (
                          <tr key={business._id}>
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>

                            <td>{business.businessName}</td>

                            <td>
                              <span
                                className={`fwt_600 ${
                                  business.status === "active"
                                    ? "f_green"
                                    : "f_red"
                                }`}
                              >
                                {capitalize(business.status)}
                              </span>
                            </td>

                            <td>
                              <div align="center">
                                <>
                                  {canView && (
                                    <button
                                      type="button"
                                      className="btn btn-link crsr_pntr mr-2 p-0 border-0"
                                      title="View"
                                      onClick={() =>
                                        handleViewBusiness(business)
                                      }
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </button>
                                  )}

                                  {canUpdate && (
                                    <button
                                      type="button"
                                      className="btn btn-link crsr_pntr mr-2 p-0 border-0"
                                      title="Edit"
                                      onClick={() =>
                                        handleEditBusiness(business)
                                      }
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </button>
                                  )}
                                  {canDelete &&
                                    business.status === "active" && (
                                      <button
                                        type="button"
                                        className="btn btn-link crsr_pntr p-0 border-0"
                                        data-toggle="modal"
                                        data-target="#modaldelete"
                                        onClick={() =>
                                          handleDeleteClick(
                                            business._id,

                                            business.businessName,
                                          )
                                        }
                                        title="Delete"
                                      >
                                        <i className="fa-solid fa-trash-can" />
                                      </button>
                                    )}
                                </>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!isLoading && businesses.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleBusinessPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showBusinessModal ? "show d-block" : ""}`}
        style={{
          backgroundColor: showBusinessModal ? "rgba(0,0,0,.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {businessId ? "Edit Business" : "Create Business"}
              </h5>

              <button
                type="button"
                className="close"
                onClick={() => setShowBusinessModal(false)}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>
                  Business Name
                  <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter business name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </div>
              {businessId && (
                <div className="form-group">
                  <label>Status</label>

                  <select
                    className="form-control"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowBusinessModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-blue"
                onClick={handleSaveBusiness}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : businessId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Business Modal */}
      <div
        className={`modal fade ${showViewModal ? "show d-block" : ""}`}
        style={{
          backgroundColor: showViewModal ? "rgba(0,0,0,.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">View Business</h5>

              <button
                type="button"
                className="close"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedBusiness(null);
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="modal-body">
              {selectedBusiness && (
                <>
                  <div className="row mb-3">
                    <div className="col-md-5">
                      <strong>Business Name</strong>
                    </div>

                    <div className="col-md-7">
                      {selectedBusiness.businessName || "-"}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-5">
                      <strong>Status</strong>
                    </div>

                    <div className="col-md-7">
                      <span
                        className={`fwt_600 ${
                          selectedBusiness.status === "active"
                            ? "f_green"
                            : "f_red"
                        }`}
                      >
                        {capitalize(selectedBusiness.status)}
                      </span>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-5">
                      <strong>Created By</strong>
                    </div>

                    <div className="col-md-7">
                      {selectedBusiness.createdBy?.name || "-"}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-5">
                      <strong>Created At</strong>
                    </div>

                    <div className="col-md-7">
                      {selectedBusiness.createdAt
                        ? new Date(selectedBusiness.createdAt).toLocaleString()
                        : "-"}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-5">
                      <strong>Updated By</strong>
                    </div>

                    <div className="col-md-7">
                      {selectedBusiness.updatedBy?.name || "-"}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-5">
                      <strong>Updated At</strong>
                    </div>

                    <div className="col-md-7">
                      {selectedBusiness.updatedAt
                        ? new Date(selectedBusiness.updatedAt).toLocaleString()
                        : "-"}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedBusiness(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Deactivate Business
              </h5>

              <button type="button" className="close" data-dismiss="modal">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to deactivate
                <br />
                <span className="fwt_600">{deleteBusinessName}</span>?
              </div>
            </div>

            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deactivating..." : "Yes, deactivate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Business;
